import { Icon } from "@atoms";
import Spinner from '@legacy/core/components/Spinner';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { faIcon } from "@utils/faIcon";
import { Component, Fragment, useEffect } from "react";
import ButtonGroup from "../../core/buttons/ButtonGroup";
import ButtonGroupRow from "../../core/buttons/ButtonGroupRow";
import UniversalButton from "../../core/buttons/UniversalButton";
import Banner from '../../core/components/Banner';
import EmailField from "../../core/fields/EmailField";
import SwitchField from "../../core/fields/SwitchField";
import { buildStripeReturnUrl, getStripeAppearance, PUBLIC_PAYMENT_ELEMENT_OPTIONS } from "../../core/utils/stripePayments";
import { formatCurrencyForServiceCompany } from "../../core/utils/utils";
import { calculateInvoiceAmounts } from "../utils/utils";


// Mounts the Payment Element and lifts the live stripe/elements pair up to the form so its Pay button can
// confirm. Lives inside <Elements>, so it can read the Stripe context through the hooks.
function PaymentElementBox({ onReady, onStripeReady }) {
    const stripe = useStripe()
    const elements = useElements()
    useEffect(() => {
        onStripeReady(stripe, elements)
    }, [stripe, elements])
    return <PaymentElement options={PUBLIC_PAYMENT_ELEMENT_OPTIONS} onReady={onReady} />
}


class InvoiceOnlinePaymentForm extends Component {

    constructor(props) {
        super(props)

        this.state = {
            errors: {},
            didHandleReturn: false,
            isConfirming: false,
            isElementReady: false,
        }
        // Captured from the Payment Element context (see PaymentElementBox) for confirm/redirect handling;
        // null until a valid receipt email creates the intent and <Elements> mounts.
        this.stripe = null
        this.elements = null
    }

    componentDidMount() {
        // Return to the previous scroll
        document.querySelector(".main").scrollTo(0, this.props.returnScroll || 0)
        // If returning from a 3DS redirect, auto-complete by capturing payment
        this.handleRedirectReturn()
    }

    componentDidUpdate(prevProps) {
        if (prevProps.clientSecret !== this.props.clientSecret) {
            // A fresh intent (the payer changed the email or method) remounts the Payment Element; wait for
            // the new element's onReady before re-enabling Pay and showing the totals. Also drop any prior
            // failure so an error from the old card/bank attempt doesn't linger under the new method.
            this.setState({ isElementReady: false, errors: {} })
        }
    }

    handleStripeReady = (stripe, elements) => {
        this.stripe = stripe
        this.elements = elements
        // Stripe arrives once the intent exists and <Elements> mounts; finish a 3DS redirect as soon as it does.
        if (stripe && !this.state.didHandleReturn) {
            this.handleRedirectReturn()
        }
    }

    handleElementReady = () => {
        this.setState({ isElementReady: true })
    }

    handlePaymentMethodTypeChange = (isBank) => {
        if (this.props.onPaymentMethodTypeChange) {
            this.props.onPaymentMethodTypeChange(isBank ? 'us_bank_account' : 'card')
        }
    }

    // A captured intent (a confirmed card or a still-processing ACH debit) is handed up to the container,
    // which records it; anything else surfaces a retryable error. Shared by the inline confirm and the
    // post-redirect return so both paths treat the intent statuses identically.
    completeCapturedIntent = (paymentIntent) => {
        const { onFormDataChange, requestAction, mode } = this.props
        if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
            onFormDataChange('payment_intent_id', paymentIntent.id, () => requestAction(mode))
            return true
        }
        return false
    }

    handleRedirectReturn = async () => {
        try {
            if (this.state.didHandleReturn || !this.stripe) {
                return
            }
            const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret')
            if (!clientSecret) {
                return
            }
            // Claim the return immediately so a second lifecycle call can't double-process the same intent.
            this.setState({ didHandleReturn: true })

            const { paymentIntent, error } = await this.stripe.retrievePaymentIntent(clientSecret)
            // Clean the querystring so a refresh doesn't reprocess the intent.
            try {
                window.history.replaceState({}, document.title, window.location.pathname)
            } catch {
                // replaceState can throw in restricted contexts; the cleanup is best-effort.
            }

            if (error) {
                this.setState({ errors: { payment_data: error.message || 'Payment failed' } })
                return
            }
            if (!this.completeCapturedIntent(paymentIntent)) {
                this.setState({ errors: { payment_data: 'Payment could not be completed. Please try another payment method.' } })
            }
        } catch (error) {
            // Surface a generic failure but allow the payer to retry.
            this.setState({ errors: { payment_data: 'We could not complete your payment. Please try again.' } })
        }
    }

    submitPayment = async () => {
        const { stripe, elements } = this
        if (!stripe || !elements) {
            return
        }

        this.setState({ errors: {}, isConfirming: true })

        // Confirm the card/bank entered in the Payment Element. `redirect: 'if_required'` keeps the payer
        // on-page unless their method needs a redirect, in which case Stripe sends them to return_url and
        // componentDidMount -> handleRedirectReturn finishes the capture on their way back.
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: buildStripeReturnUrl(),
                // The Payment Element collects no email (email: 'never') so the receipt address can be set as
                // the intent's receipt_email at creation; re-supply it here as billing_details.email, which
                // ACH requires for the mandate.
                payment_method_data: { billing_details: { email: this.props.receiptEmail } },
            },
            redirect: 'if_required',
        })

        if (error) {
            this.setState({ errors: { payment_data: error.message || 'Payment failed' }, isConfirming: false })
            return
        }
        // On capture, leave isConfirming true so the button stays disabled until the container navigates away.
        if (!this.completeCapturedIntent(paymentIntent)) {
            this.setState({ errors: { payment_data: 'Payment could not be completed. Please try another payment method.' }, isConfirming: false })
        }
    }

    renderButtons = () => {
        const { submitting, invoice, errors, switchToPrimaryForm } = this.props
        const { amountDue } = calculateInvoiceAmounts(invoice)

        if (errors.unexpectedError) {
            return (
                <div className="data-panel__action-feedback">
                    <span className="text-invalid"><strong>An unexpected error occurred.</strong></span>
                </div>
            )
        }
        else {
            if (submitting) {
                return <Spinner centered={true} />
            }
            else {
                return (
                    <ButtonGroup className="button-group--data-panel-actions">
                        <ButtonGroupRow>
                            <UniversalButton type="primary" text={this.state.isConfirming ? 'Processing…' : `Pay ${formatCurrencyForServiceCompany(amountDue, invoice.service_company)} Now`} handler={this.submitPayment} disabled={this.state.isConfirming || !this.state.isElementReady || invoice.awaiting_automated_tax} />
                        </ButtonGroupRow>
                        <ButtonGroupRow>
                            <UniversalButton type="secondary" text="Cancel" handler={event => switchToPrimaryForm()} />
                        </ButtonGroupRow>
                    </ButtonGroup>
                )
            }
        }
    }

    render() {
        const { invoice, clientSecret, stripePromise, receiptEmail, receiptEmailInput, receiptEmailError, onReceiptEmailChange } = this.props
        const { amountDue } = calculateInvoiceAmounts(invoice)
        const paymentMethodType = this.props.paymentMethodType || 'card'
        const isBank = paymentMethodType === 'us_bank_account'
        const isTestMode = window.STRIPE_PUBLISHABLE_KEY && window.STRIPE_PUBLISHABLE_KEY.includes("test")
        const appearance = getStripeAppearance()

        return (
            <Fragment>
                <div className="data-panel-container data-panel-container--with-margin">
                    <div className="data-panel" aria-label="Pay Online">
                        <div className="data-panel__form data-panel__form--no-margin" aria-label="Pay Online Form">
                            <fieldset>
                                <legend>
                                    Pay with {isBank ? 'Bank Account (ACH)' : 'Card'} {isTestMode && <span className="stripe-test-signifier">Test Mode</span>}
                                </legend>
                                <p className="data-panel__form__caption">
                                    {isBank
                                        ? (<span>Provide your bank account to pay via ACH. Bank details are securely handled by <a className="text-link text-link--inline text-link--inline-stripe" href="https://stripe.com" target="_blank" rel="noopener"><Icon containerClassName="icon-inline" icon={faIcon({ name: "cc-stripe", style: "brands" })} className="inline-stripe" aria-label="Stripe" /></a>. ACH payments typically process within 3–5 business days.</span>)
                                        : (<span>Add your card details below to pay this invoice online. Your data is securely managed by our payments partner, stripe <a className="text-link text-link--inline text-link--inline-stripe" href="https://stripe.com" target="_blank" rel="noopener"><Icon containerClassName="icon-inline" icon={faIcon({ name: "cc-stripe", style: "brands" })} className="inline-stripe" aria-label="Stripe" /></a>.</span>)}
                                </p>

                                <SwitchField
                                    fieldName="payment_method_type_toggle"
                                    fieldLabel="Payment Method"
                                    fieldValue={isBank}
                                    fieldOnChange={this.handlePaymentMethodTypeChange}
                                    disabled={this.state.isConfirming}
                                    checkedText="Bank Account"
                                    uncheckedText="Credit/Debit Card"
                                    errors={this.state.errors}
                                />

                                <EmailField
                                    fieldName="receipt_email"
                                    fieldLabel={<Fragment>Email address <span className="text-optional">(for your receipt)</span></Fragment>}
                                    fieldValue={receiptEmailInput}
                                    fieldOnChange={onReceiptEmailChange}
                                    errors={{ receipt_email: receiptEmailError }}
                                />

                                {invoice.awaiting_automated_tax && (
                                    <Banner type="info" text="Your accounting system hasn't applied sales tax to this invoice yet. Please wait a few moments." />
                                )}

                                {receiptEmail && !invoice.awaiting_automated_tax && (
                                    <div className="data-panel__form__field" id="div_id_payment_data" aria-label={isBank ? 'Bank Information' : 'Card Information'}>
                                        <label htmlFor="id_payment_data" className="data-panel__form__field__label in-form">{isBank ? 'Bank Information' : 'Card Information'}</label>
                                        <div className="data-panel__form__field__input data-panel__form__field__input--stripe-elements">
                                            {clientSecret ? (
                                                <Fragment>
                                                    <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret, appearance }}>
                                                        <PaymentElementBox onReady={this.handleElementReady} onStripeReady={this.handleStripeReady} />
                                                    </Elements>
                                                    {!this.state.isElementReady && <Spinner centered={true} />}
                                                </Fragment>
                                            ) : (
                                                <Spinner centered={true} />
                                            )}
                                        </div>
                                        {this.state.errors.payment_data && <div className="data-panel__form__field__errors" aria-label="Field Errors">{this.state.errors.payment_data}</div>}
                                    </div>
                                )}

                                {this.state.isElementReady && isBank && (
                                    <Banner
                                        type="info"
                                        text={
                                            <span>
                                                By providing your bank account information and confirming this payment, you authorize <strong>{invoice.service_company.name}</strong> to debit the bank account specified for the amount owed. This authorization will remain in effect for future payments.
                                            </span>
                                        }
                                    />
                                )}
                                {this.state.isElementReady && (
                                    <Banner type="info" text={<span>You're paying <strong>{formatCurrencyForServiceCompany(amountDue, invoice.service_company)} {invoice.service_company.currency_code}</strong> to <strong>{invoice.service_company.name}</strong> for Invoice <strong>{invoice.custom_id ? invoice.custom_id : invoice.id}</strong></span>} />
                                )}
                            </fieldset>
                        </div>
                        {this.renderButtons()}
                    </div>
                </div>
                {window.USING_PUBLIC_URL === true && (
                    <div className="powered-by-panel">
                        <span>Powered By</span>
                        <img className="logo--grey" src={window.LOGOTYPE_URL} width="148px" alt="Roopairs"></img>
                    </div>
                )}
            </Fragment>
        )
    }
}

export default InvoiceOnlinePaymentForm
